import { readonly, ref } from 'vue';
import HTTP from '@/utils/http';
import { useRoute } from 'vue-router/composables';
import { useCsrf } from '@/composables/integrations/slack';
import { t } from '@/locales';

const STATE_SEPARATOR = '.';

export function useAuthorise() {
  const route = useRoute();

  const {
    check: checkCsrf,
  } = useCsrf();

  const {
    code,
    state,
    error: slackError,
  } = route.query;

  const isProcessing = ref(false);
  const isValidRequest = ref(true);
  const [
    displayUuid,
    csrf,
  ] = String(state).split(STATE_SEPARATOR);

  function isQueryValid() {
    if (!code || String(code).trim() === '') {
      return false;
    }
    if (!displayUuid || String(displayUuid).trim() === '') {
      return false;
    }
    if (!csrf || String(csrf).trim() === '') {
      return false;
    }

    return true;
  }

  function wasAccessDeniedByUser() {
    return slackError && slackError === 'access_denied';
  }

  function isCsrfValid() {
    return isValidRequest.value && checkCsrf(csrf);
  }

  async function authorise() {
    try {
      if (wasAccessDeniedByUser()) {
        throw Error(t('integrations.slack.pages.authorise.denied_request'));
      }
      if (!isQueryValid()) {
        throw Error(t('integrations.slack.pages.authorise.invalid_query'));
      }
      if (!isCsrfValid()) {
        throw Error(t('integrations.slack.pages.authorise.invalid_csrf'));
      }

      isProcessing.value = true;

      await HTTP({
        url: `integrations/slack/${displayUuid}/authorise`,
        method: 'patch',
        data: {
          code,
          csrf,
        },
        suppressAllErrors: true,
      });
    } catch (err) {
      // TODO: improve handling

      isValidRequest.value = false;

      throw err;
    } finally {
      isProcessing.value = false;
    }
  }

  return {
    isProcessing: readonly(isProcessing),
    isValidRequest: readonly(isValidRequest),
    isQueryValid,
    wasAccessDeniedByUser,
    isCsrfValid,
    authorise,
  };
}
